/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  EXITS_NODES_TASK_REGISTER_PROCESSES,
  NEW_NODES_TASK_REGISTER_PROCESSES,
  PHASE_SELECT_JOB,
  PHASE_SELECT_TASK,
  PHASE_TASK_CONFIRM,
  PHASE_TASK_SETTING,
  TaskControlState,
} from "../consts";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const useJobsetInputPhase = (initialPhases: string[]) => {
  const { t } = useTranslation();
  const panelTitleMap: { [key: string]: string } = useMemo(() => {
    return {
      [PHASE_SELECT_TASK]: t("AWVUI-MSG115"),
      [PHASE_SELECT_JOB]: t("AWVUI-MSG202"),
      [PHASE_TASK_SETTING]: t("AWVUI-MSG117"),
      [PHASE_TASK_CONFIRM]: t("AWVUI-MSG118"),
    };
  }, [t]);

  const createTaskControlState = useCallback(
    (initialProcess: string[], initialPhase?: number) => {
      return {
        process: initialProcess,
        processTitles: initialProcess.map((item) => panelTitleMap[item]),
        phase: initialPhase ?? 0,
      };
    },
    [panelTitleMap]
  );
  const setPhases = useCallback(
    (phases: string[]) => {
      setTaskControlState(createTaskControlState(phases));
    },
    [createTaskControlState]
  );

  const [taskControlState, setTaskControlState] = useState<TaskControlState>(
    createTaskControlState(initialPhases)
  );

  /**
   * go next task control phase
   */
  const goNext = useCallback(() => {
    setTaskControlState((current) => {
      if (current.phase === current.process.length - 1) {
        // If current phase is a last phase, do nothing.
        return current;
      } else {
        // Otherwise, Update phase to next
        const process =
          current.phase === 0 &&
          current.process === NEW_NODES_TASK_REGISTER_PROCESSES
            ? EXITS_NODES_TASK_REGISTER_PROCESSES
            : current.process;
        return createTaskControlState(process, current.phase + 1);
      }
    });
  }, [createTaskControlState]);

  /**
   * go back task control phase
   */
  const goBack = useCallback(() => {
    setTaskControlState((current) => {
      if (current.phase === 0) {
        return current;
      } else {
        // Update phase to previous
        return {
          ...current,
          phase: current.phase - 1,
        };
      }
    });
  }, []);

  /**
   * go to Selecting Point Phase
   */
  const goNew = useCallback(() => {
    setTaskControlState((current) => {
      const newState: TaskControlState = {
        process: NEW_NODES_TASK_REGISTER_PROCESSES,
        processTitles: NEW_NODES_TASK_REGISTER_PROCESSES.map(
          (p) => panelTitleMap[p]
        ),
        phase: current.phase + 1,
      };
      return newState;
    });
  }, [panelTitleMap]);

  /**
   * get current phase.
   * @return {string} current phase
   */
  const getCurrentPhase = useCallback(() => {
    // return "";
    return taskControlState.process[taskControlState.phase];
  }, [taskControlState.phase, taskControlState.process]);

  return {
    setPhases,
    taskControlState,
    goNext,
    goBack,
    goNew,
    currentPhase: getCurrentPhase(),
  };
};
